/**
 * Script Utility Functions
 * 
 * Provides utilities for resolving script types with priority system:
 * Game-specific → Company default → User preference → System default
 */

import { getScriptByCode, getScriptDirection, isRTLScript, SCRIPT_TYPES } from '../dataSources/scriptTypes';
import type { Game, Company, UserProfile } from '../stores/storeTypes';

/**
 * Default script when no preference is set
 */
const DEFAULT_SCRIPT = 'latin';

/**
 * Get the current effective script based on priority system
 * 
 * Priority order:
 * 1. Game-specific setting (if game is selected and has scriptType)
 * 2. Company default (if company is selected and has scriptType)
 * 3. User preference (from user profile settings)
 * 4. System default ('latin')
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns The effective script code
 */
export function getCurrentScript(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): string {
  // Priority 1: Game-specific setting
  if (game?.settings?.scriptType) {
    return game.settings.scriptType;
  }

  // Priority 2: Company default
  if (company?.settings?.scriptType) {
    return company.settings.scriptType;
  }

  // Priority 3: User preference
  if (user?.settings?.scriptType) {
    return user.settings.scriptType;
  }

  // Priority 4: System default
  return DEFAULT_SCRIPT;
}

/**
 * Get the effective text direction based on current script
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns 'ltr' or 'rtl'
 */
export function getEffectiveDirection(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): 'ltr' | 'rtl' {
  const scriptCode = getCurrentScript(game, company, user);
  return getScriptDirection(scriptCode);
}

/**
 * Check if current effective script is RTL
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns true if RTL, false otherwise
 */
export function isCurrentScriptRTL(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): boolean {
  const scriptCode = getCurrentScript(game, company, user);
  return isRTLScript(scriptCode);
}

/**
 * Get recommended font for current script
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns CSS font-family string
 */
export function getScriptFont(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): string {
  const scriptCode = getCurrentScript(game, company, user);
  const script = getScriptByCode(scriptCode);
  
  if (!script?.fontRecommendations || script.fontRecommendations.length === 0) {
    return 'sans-serif';
  }

  // Return as CSS font-family value with fallback
  return `${script.fontRecommendations.join(', ')}, sans-serif`;
}

/**
 * Get script override information (for displaying in UI)
 * Returns information about which level is setting the script
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns Object with override information
 */
export function getScriptOverrideInfo(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): {
  scriptCode: string;
  source: 'game' | 'company' | 'user' | 'default';
  isOverridden: boolean;
  overriddenBy?: 'game' | 'company';
} {
  let scriptCode: string;
  let source: 'game' | 'company' | 'user' | 'default';
  let overriddenBy: 'game' | 'company' | undefined;

  if (game?.settings?.scriptType) {
    scriptCode = game.settings.scriptType;
    source = 'game';
    overriddenBy = undefined;
  } else if (company?.settings?.scriptType) {
    scriptCode = company.settings.scriptType;
    source = 'company';
    overriddenBy = undefined;
  } else if (user?.settings?.scriptType) {
    scriptCode = user.settings.scriptType;
    source = 'user';
    // Check if it would be overridden
    if (game?.settings?.scriptType) {
      overriddenBy = 'game';
    } else if (company?.settings?.scriptType) {
      overriddenBy = 'company';
    }
  } else {
    scriptCode = DEFAULT_SCRIPT;
    source = 'default';
    // Check if it would be overridden
    if (game?.settings?.scriptType) {
      overriddenBy = 'game';
    } else if (company?.settings?.scriptType) {
      overriddenBy = 'company';
    }
  }

  return {
    scriptCode,
    source,
    isOverridden: overriddenBy !== undefined,
    overriddenBy
  };
}

/**
 * Get a user-friendly message about script settings
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns Human-readable description of current script setting
 */
export function getScriptSettingMessage(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): string {
  const info = getScriptOverrideInfo(game, company, user);
  const script = getScriptByCode(info.scriptCode);
  const scriptName = script?.name || info.scriptCode;

  switch (info.source) {
    case 'game':
      return `Using ${scriptName} script (game setting)`;
    case 'company':
      return `Using ${scriptName} script (company default)`;
    case 'user':
      return `Using ${scriptName} script (your preference)`;
    case 'default':
      return `Using ${scriptName} script (system default)`;
    default:
      return `Using ${scriptName} script`;
  }
}

/**
 * Format script selection options with current/override indicators
 * Useful for dropdown menus showing which script is currently active
 * 
 * @param game - Current selected game (optional)
 * @param company - Current selected company (optional)
 * @param user - Current user profile (optional)
 * @returns Array of script options with metadata
 */
export function getScriptOptionsWithStatus(
  game?: Game | null,
  company?: Company | null,
  user?: UserProfile | null
): Array<{
  value: string;
  label: string;
  direction: 'ltr' | 'rtl';
  isCurrent: boolean;
  isGameDefault?: boolean;
  isCompanyDefault?: boolean;
  isUserPreference?: boolean;
}> {
  const currentScript = getCurrentScript(game, company, user);
  const gameScript = game?.settings?.scriptType;
  const companyScript = company?.settings?.scriptType;
  const userScript = user?.settings?.scriptType;

  return SCRIPT_TYPES.map(script => ({
    value: script.code,
    label: script.nativeName ? `${script.name} (${script.nativeName})` : script.name,
    direction: script.direction,
    isCurrent: script.code === currentScript,
    isGameDefault: script.code === gameScript,
    isCompanyDefault: script.code === companyScript,
    isUserPreference: script.code === userScript
  }));
}

/**
 * Validate if a script code is valid
 * 
 * @param code - Script code to validate
 * @returns true if valid, false otherwise
 */
export function validateScriptCode(code: string | undefined): boolean {
  if (!code) return false;
  return getScriptByCode(code) !== undefined;
}

/**
 * Get default script code
 */
export function getDefaultScript(): string {
  return DEFAULT_SCRIPT;
}

